import { cloneDeep } from 'lodash'
import { getNextEntityId } from '../getNextEntityId'
import { addNotification } from '../addNotification'
import {
  TMSService,
  TMSServiceQuantity,
  TMSServiceWithOmits,
} from './../../types/msService/models'
import { getSupplierProductEntitySelectItemFromSupplierProduct } from './SupplierProduct'
import type {
  TEntityId,
  ISelectItem,
  IEntitySelectItem,
  IStateItem,
} from '~/types/models'
import type { ISupplierProductWithItemsPropertyPopulated } from '~/types/supplierProduct/models'
import type { TProduct, IProductKitItem } from '@/types/product/models'
import type { ISupplierProductItem } from '~/types/supplierProductItem/models'
import type { ITsd } from '~/types/tsd/models'

import {
  assertIsNotUndefined,
  assertIsDefined,
  assertIsString,
} from '~/types/models'
import { ProductLevelIds } from '~/services/selectLists/productLevels'
import * as KitItem from '~/cynqueModules/dataObjects/KitItem'
import { getKitItemsForProduct } from '~/services/products'
import { isKitProduct, isSetKitProduct } from '@/types/product/models'
import { getSupplierProductWithItems } from '~/services/supplierProducts'
import {
  ItemAvailabilityIds,
  getItemAvailabilityStateById,
  doesQualifyToBeHidden,
} from '~/cynqueModules/dataObjects/ItemAvailabilityStatus'
import { resolveMSServiceTerm } from '~/cynqueModules/dataObjects/MSServiceTerm'

const mn = '[cynqueModules/dataObjects/KitService.ts]'

// ==================================================================

// Public Interface -------------------------------------------------

export function getServicesForKitServiceFromProvidedServices<
  I extends TMSService
>({
  callingFn = undefined,
  providedServices,
  kitService,
  doIncludeKitService = false,
  doIncludeDoesQualifyToBeHidden = false,
}: {
  callingFn?: string | undefined
  providedServices: I[]
  kitService: I
  doIncludeKitService: boolean
  doIncludeDoesQualifyToBeHidden: boolean
}): I[] {
  const fn = `${
    callingFn ? callingFn + ' ---> ' : ''
  }${mn} getServicesForKitServiceFromProvidedServices |`
  const theseServicesForKitItems: I[] = doIncludeKitService ? [kitService] : []

  const kitItemServices = providedServices.filter(
    (s: I) =>
      s.parentMSServiceId === kitService.id &&
      (doIncludeDoesQualifyToBeHidden ||
        (!doIncludeDoesQualifyToBeHidden && !doesQualifyToBeHidden(s)))
  )
  // console.debug(`${callingFn} *********************************`)
  // console.debug('kit service:', kitService)
  // console.debug(kitItemServices)
  // console.debug(`${callingFn} *********************************`)
  for (const service of kitItemServices) {
    if (!isKitProduct(service.product)) {
      // console.debug(`${callingFn} !isKitProduct ${service.product.id}`)
      theseServicesForKitItems.push(service)
    } else {
      // console.debug(`${callingFn} isKitProduct ${service.product.id}`)
      theseServicesForKitItems.push(
        ...getServicesForKitServiceFromProvidedServices({
          callingFn,
          providedServices,
          kitService: service,
          doIncludeKitService,
          doIncludeDoesQualifyToBeHidden,
        })
      )
    }
  }
  console.debug(`${fn} theseServicesForKitItems...`)
  console.dir(theseServicesForKitItems)
  return theseServicesForKitItems
}

// ==================================================================

// Public Interface -------------------------------------------------

export function buildKitService({
  callingMn,
  rootEntity,
  location,
  supplier,
  tsd,
  doInclude,
  masterService,
  origin,
  quantity,
  closeByDate,
  status,
  product,
  parentMSServiceId,
  parentProduct,
}: {
  callingMn: string
  rootEntity: string
  location: ISelectItem
  supplier: ISelectItem
  // Copied onto every kit item (KitItem.buildServiceForKitItem).
  tsd?: ITsd | null
  doInclude: boolean
  masterService: ISelectItem
  origin: ISelectItem
  quantity: TMSServiceQuantity // CHECK THIS
  closeByDate: string
  status: ISelectItem
  product: TProduct
  parentMSServiceId: TEntityId | undefined
  parentProduct: TProduct | undefined
}): TMSServiceWithOmits {
  const fn = `${callingMn} ---> ${mn} buildKitService |`
  const entityName = `${rootEntity}MSService`
  const newId = getNextEntityId({ entityName })
  const kitService = {
    id: newId,
    masterService,
    product: cloneDeep(product), // To avoid aliasing problems
    parentProduct,
    parentMSServiceId,
    descr: product.descr, // default value; will be replaced by supplierProdut or supplierProductItem descr if applicable
    origin,
    location,
    supplier,
    tsd: tsd ?? undefined,
    recursPaidDescr: '',
    quantity,
    splitFraction: 0,
    unitRcAmount: 0,
    mrcAmount: 0,
    unitNrcAmount: 0,
    nrcAmount: 0,
    oneTimeAmount: 0,
    tcvAmount: 0,
    closeByDate,
    status,
    doInclude,
    canBeDeleted: true,
  }
  console.debug(`${fn} kitService...`)
  console.dir(cloneDeep(kitService))
  return kitService
}

// (Recursion head)
/**
 * One orderable unit of a kit level: a single row, or a nested kit's whole
 * subtree with its header first.
 */
interface ILevelEntry {
  rows: TMSServiceWithOmits[]
  /**
   * Set only for a nested kit, whose header never receives a
   * `supplierProductItem`. For a plain row the key is read off the row itself
   * after the merge.
   */
  seqNum?: number
}

/**
 * Flatten one kit level into `supplierProductItem.seqNum` order.
 *
 * ⚠ ENTRIES, NOT ROWS. Sorting the flattened array would scatter a nested kit's
 * children away from its header, and the msServices card lays rows out by
 * ADJACENCY — see sortMSServicesByKitTopology. Each entry moves as a block.
 *
 * ⚠ An entry with no usable seqNum sorts LAST and ties fall back to arrival
 * order, so a supplier-product whose items carry none produces exactly the order
 * it produced before — which is what makes this safe to adopt on catalogues that
 * predate the field being populated.
 *
 * This is where the ordering is CHEAP: the supplier-product tree is already in
 * hand here because the build fetched it. At load time it is not, which is why
 * sortMSServicesByKitTopology has to infer slots from the rows instead.
 */
export function orderLevelEntriesBySeqNum(
  entries: ILevelEntry[]
): TMSServiceWithOmits[] {
  return entries
    .map((entry, index) => {
      const seqNum =
        typeof entry.seqNum === 'number'
          ? entry.seqNum
          : entry.rows[0]?.supplierProductItem?.seqNum
      return {
        entry,
        index,
        seqNum: typeof seqNum === 'number' ? seqNum : Number.MAX_SAFE_INTEGER,
      }
    })
    .sort((a, b) =>
      a.seqNum === b.seqNum ? a.index - b.index : a.seqNum - b.seqNum
    )
    .flatMap(({ entry }) => entry.rows)
}

export async function buildServicesForKitService({
  rootEntity,
  kitService,
  supplierProductWithItems,
  supplierProductOptionalItemsToInclude,
  defaultTerm,
  parentSupplierProductItemPath,
  triggeringSupplierProductItem,
}: {
  rootEntity: string
  kitService: TMSServiceWithOmits
  supplierProductWithItems:
    | ISupplierProductWithItemsPropertyPopulated
    | undefined
  supplierProductOptionalItemsToInclude: ISelectItem[]
  // The service group's defaultTerm; see resolveMSServiceTerm. The kit header
  // row itself takes no term -- its displayed term is its items' roll-up.
  defaultTerm: unknown
  parentSupplierProductItemPath?: string
  triggeringSupplierProductItem?: ISupplierProductItem
}) {
  const fn = `${mn} buildServicesForKitService |`
  const theseServicesForKitItems: TMSServiceWithOmits[] = []
  const currentLevelNonKitServices: TMSServiceWithOmits[] = []
  // This level's rows, kept as ORDERABLE UNITS rather than pushed flat, so the
  // seqNum ordering below cannot detach a nested kit's subtree from its header.
  // A plain item is a unit of one; a nested kit is its whole returned block.
  const levelEntries: ILevelEntry[] = []
  let priorSupplierProductItem: ISupplierProductItem | undefined

  if (!kitService.product.doExcludeKitServiceWhenBuildingServices) {
    const parentEntitySelectItem: IEntitySelectItem | undefined =
      triggeringSupplierProductItem?.childSupplierProduct
        ? {
            id: triggeringSupplierProductItem.id,
            label: triggeringSupplierProductItem.descr || 'Not Set',
          }
        : undefined
    const supplierProductEntitySelectItem =
      getSupplierProductEntitySelectItemFromSupplierProduct(
        supplierProductWithItems,
        parentEntitySelectItem
      )
    if (supplierProductEntitySelectItem) {
      kitService.supplierProduct = supplierProductEntitySelectItem
      const parentLabel = kitService.supplierProduct.parent?.label
      kitService.descr =
        parentLabel && parentLabel !== 'Not Set'
          ? parentLabel
          : kitService.supplierProduct.label
    } else {
      kitService.descr = kitService.product.descr
    }
    theseServicesForKitItems.push(kitService)
  }

  console.debug('***************')
  console.debug(`${fn} Called with:`)
  console.debug(`${fn} theseServicesForKitItems:`, theseServicesForKitItems)
  console.debug(`${fn} kitService: `, kitService)
  console.debug(
    `${fn} kitService.product.doExcludeKitServiceWhenBuildingServices:`,
    kitService.product.doExcludeKitServiceWhenBuildingServices
  )
  console.debug(`${fn} SupplierProductWithItems: `, supplierProductWithItems)
  console.debug(
    `${fn} supplierProductWithOptionalItemsToInclude:`,
    supplierProductOptionalItemsToInclude
  )
  console.debug('***************')

  const remainingSPItems = [...(supplierProductWithItems?.items || [])]
  const kitItems = await getKitItems({
    callingMn: fn,
    product: kitService.product,
  })
  for (const [i, item] of kitItems.entries()) {
    const newOMSS = KitItem.buildServiceForKitItem({
      rootEntity,
      kitService,
      item,
    })
    if (!isKitProduct(newOMSS.product)) {
      // kitItem's product is an ITEM, not a KIT
      levelEntries.push({ rows: [newOMSS] })
      currentLevelNonKitServices.push(newOMSS)
    } else {
      // kitItem's product is a KIT (this is a nested Kit)
      const matchIndex = remainingSPItems.findIndex(
        (spi: ISupplierProductItem) => spi.product.id === newOMSS.product.id
      )
      const currentSupplierProductItem =
        matchIndex >= 0 ? remainingSPItems.splice(matchIndex, 1)[0] : undefined

      console.debug(`${fn} matchIndex:`, matchIndex)
      console.debug(`${fn} remainingSPItems:`, remainingSPItems)
      console.debug(
        `${fn} currentSupplierProductItem:`,
        currentSupplierProductItem
      )

      // ⚠ AN OPTIONAL SET OR KIT THE USER LEFT OUT BUILDS NOTHING -- not its
      // header (a Kit's), and not its mandatory contents. Those rows exist
      // only because it was taken. Its optional contents would be dropped
      // anyway (their picks went with it: dropPicksUnderUnpickedOptions); the
      // mandatory ones, and a Kit's header, are why this check is here.
      if (
        currentSupplierProductItem?.isOptional &&
        !_isOptionPicked(
          supplierProductOptionalItemsToInclude,
          currentSupplierProductItem,
          parentSupplierProductItemPath
        )
      ) {
        continue
      }

      const nestedKitRows = await buildServicesForNestedKitHeader({
        rootEntity,
        kitHeader: newOMSS,
        supplierProductItem: currentSupplierProductItem,
        supplierProductOptionalItemsToInclude,
        defaultTerm,
        parentSupplierProductItemPath,
      })
      // ⚠ AN OPTIONAL KIT TAKEN WITH NOTHING IN IT BUILDS NOTHING EITHER --
      // its contents all optional (a Set's included), none of them picked.
      // Its header alone would be an empty row. The options choosers refuse
      // this (cynqueModules/kitOptionContent.ts); this is the builder's half.
      // Checked after the build, not before, because "nothing inside" has to
      // see through nested Kits and Sets.
      //
      // So is a Kit the supplier does not carry at all (no slot in its
      // supplier-product): its contents are all NOT_AVAILABLE, dropped by the
      // filter at the end of its own build, so it too is just a header -- and
      // with no supplier-product, it has no options dialog to fill it later.
      //
      // ⚠ A MANDATORY Kit is built even when empty. Its options are chosen
      // later from the options dialog on ITS OWN ROW -- the parent's dialog
      // does not reach into it (doRecurseIntoOptionScope) -- so without the
      // header they could never be chosen at all. Jerry's call, 2026-10-17.
      if (
        (!currentSupplierProductItem ||
          currentSupplierProductItem.isOptional) &&
        !hasRowsBeneathKitHeader(newOMSS, nestedKitRows)
      ) {
        continue
      }

      levelEntries.push({
        rows: nestedKitRows,
        // ⚠ Captured HERE because it cannot be recovered later: a nested kit's
        // header row never goes through the merge below (it skips kit products),
        // so it never gets a `supplierProductItem` -- and must not, because the
        // API refuses a row with both that and the `supplierProduct` the header
        // does carry. This is the one moment the pairing between the kit row
        // and its slot is known.
        seqNum: currentSupplierProductItem?.seqNum,
      })
    }
  }
  if (supplierProductWithItems?.items) {
    const spiCountByProductId = new Map<string, number>()
    for (const spi of supplierProductWithItems.items) {
      if (!isKitProduct(spi.product)) {
        const pid = spi.product.id
        spiCountByProductId.set(pid, (spiCountByProductId.get(pid) || 0) + 1)
      }
    }
    const serviceCountByProductId = new Map<string, number>()
    for (const serv of currentLevelNonKitServices) {
      const pid = serv.product.id
      serviceCountByProductId.set(
        pid,
        (serviceCountByProductId.get(pid) || 0) + 1
      )
    }
    for (const [productId, spiCount] of spiCountByProductId) {
      const serviceCount = serviceCountByProductId.get(productId) || 0
      const deficit = spiCount - serviceCount
      if (deficit > 0) {
        const templateKitItem = kitItems.find(
          (ki: IProductKitItem) => ki.product.id === productId
        )
        if (templateKitItem) {
          for (let d = 0; d < deficit; d++) {
            const extraService = KitItem.buildServiceForKitItem({
              rootEntity,
              kitService,
              item: templateKitItem,
            })
            levelEntries.push({ rows: [extraService] })
            currentLevelNonKitServices.push(extraService)
          }
        }
      }
    }
  }
  // Merge SPI data into current-level non-kit services only.
  // Nested kit recursions already merged their own services.
  await _mergeSupplierProductItemDataIntoServicesForKitItems(
    supplierProductWithItems,
    supplierProductOptionalItemsToInclude,
    currentLevelNonKitServices,
    kitService,
    parentSupplierProductItemPath,
    triggeringSupplierProductItem?.descr,
    defaultTerm
  )
  // Only NOW can this level be ordered: the merge above is what attaches each
  // plain row's `supplierProductItem`, and its seqNum is the sort key. The kit
  // header (already in theseServicesForKitItems, or absent for a Set) stays
  // first; everything else follows in supplier-product order.
  theseServicesForKitItems.push(...orderLevelEntriesBySeqNum(levelEntries))
  // ⚠ WHAT DOES NOT GET PERSISTED. An option the user declined is no longer
  // built into a row: only MANDATORY items and the options actually chosen
  // survive. It used to be that every alternative in the whole supplier-product
  // tree became a hidden OPTIONAL_NOT_INCLUDED msService -- roughly half the
  // rows a kit produced -- so that "Add Available" could flip one back on
  // later. Changing a choice now goes through the options dialog, which reads
  // the alternatives from the supplier-product rather than from stored rows,
  // so those rows have no reader left.
  //
  // Expressed through doesQualifyToBeHidden rather than by naming ids: the set
  // of states that must not be persisted is exactly the set that would never be
  // shown, and stating it once keeps the two from drifting. NOT_AVAILABLE was
  // already dropped here, which is why no msService anywhere carries it.
  //
  // The first clause stands because a ROOT kit, and a nested kit its supplier
  // does not carry, have no itemAvailabilityState at all. (A nested plain Kit
  // built from a supplier-product item carries MANDATORY or OPTIONAL_INCLUDED;
  // see buildServicesForNestedKitHeader.)
  const filteredArray = theseServicesForKitItems.filter(
    (mss) =>
      !mss.itemAvailabilityState ||
      !doesQualifyToBeHidden(mss as unknown as TMSService)
  )
  console.debug(
    `${fn} returning filtered version of theseServicesForKitItems...`
  )
  console.dir(filteredArray)
  return filteredArray
}

/**
 * A nested kit's subtree: its header (already built from its slot by
 * KitItem.buildServiceForKitItem, and passed in as `kitHeader`), then
 * everything beneath it.
 *
 * Shared by the create-time build above and by the options dialog, which
 * builds a whole optional plain Kit the user has just taken
 * (buildMSServicesForKitOption) -- so a Kit added after the fact is built
 * exactly as Add by Supplier would have built it.
 *
 * ⚠ A PLAIN KIT'S HEADER RECORDS THE ANSWER: MANDATORY, or OPTIONAL_INCLUDED
 * when its item is optional (and so was picked -- the caller skips one that
 * was not). It is a row like any item's, and the options dialog supersedes and
 * revives it like one. A Set's header is never built, so it gets nothing.
 *
 * `parentSupplierProductItemPath` is the path of the kit's PARENT, exactly as
 * buildServicesForKitService receives it; the kit's own id is appended here.
 */
export async function buildServicesForNestedKitHeader({
  rootEntity,
  kitHeader,
  supplierProductItem,
  supplierProductOptionalItemsToInclude,
  defaultTerm,
  parentSupplierProductItemPath,
}: {
  rootEntity: string
  kitHeader: TMSServiceWithOmits
  /** The kit's slot in its parent's supplier-product, if the supplier has one. */
  supplierProductItem: ISupplierProductItem | undefined
  supplierProductOptionalItemsToInclude: ISelectItem[]
  defaultTerm: unknown
  parentSupplierProductItemPath: string | undefined
}): Promise<TMSServiceWithOmits[]> {
  const fn = `${mn} buildServicesForNestedKitHeader |`
  let nextSupplierProductWithItems:
    | ISupplierProductWithItemsPropertyPopulated
    | undefined

  if (supplierProductItem) {
    kitHeader.kitQuantity = supplierProductItem.quantity
    if (!supplierProductItem.childSupplierProduct) {
      kitHeader.itemAvailabilityState = getItemAvailabilityStateById(
        ItemAvailabilityIds.NOT_AVAILABLE
      )
    } else {
      if (!isSetKitProduct(kitHeader.product)) {
        kitHeader.itemAvailabilityState = getItemAvailabilityStateById(
          supplierProductItem.isOptional
            ? ItemAvailabilityIds.OPTIONAL_INCLUDED
            : ItemAvailabilityIds.MANDATORY
        )
      }
      console.debug(
        `${fn} getting next supplierProductWithItemsPropertyPopulated for:`,
        supplierProductItem.childSupplierProduct
      )
      nextSupplierProductWithItems =
        await getSupplierProductWithItemsPropertyPopulated({
          id: supplierProductItem.childSupplierProduct.id,
          label: supplierProductItem.childSupplierProduct.label
            ? supplierProductItem.childSupplierProduct.label
            : `${supplierProductItem.childSupplierProduct.id} has no label`, // TEMPORARY. JR.
        })
      console.debug(`${fn} got:`, nextSupplierProductWithItems)
    }
  }

  return buildServicesForKitService({
    rootEntity,
    kitService: kitHeader,
    supplierProductWithItems: nextSupplierProductWithItems,
    supplierProductOptionalItemsToInclude,
    defaultTerm,
    parentSupplierProductItemPath: parentSupplierProductItemPath
      ? `${parentSupplierProductItemPath}>${supplierProductItem?.id}`
      : supplierProductItem?.id,
    triggeringSupplierProductItem: supplierProductItem,
  })
}

/**
 * Does a nested kit's built subtree hold any row besides its own header? (A
 * Set has no header, so for a Set: any row at all.) A mandatory Kit beneath it
 * counts -- its header is always built. See the check in
 * buildServicesForKitService and its twin in the Add Kit flow (msServicesCard's
 * addMSServiceKit).
 */
export function hasRowsBeneathKitHeader(
  kitHeader: TMSServiceWithOmits,
  rows: TMSServiceWithOmits[]
): boolean {
  return rows.some((row) => row !== kitHeader)
}

// Public Interface End ---------------------------------------------

async function _mergeSupplierProductItemDataIntoServicesForKitItems(
  supplierProductWithItems:
    | ISupplierProductWithItemsPropertyPopulated
    | undefined,
  supplierProductOptionalItemsToInclude: ISelectItem[],
  servicesForKitItems: TMSServiceWithOmits[],
  kitService: TMSServiceWithOmits,
  parentSupplierProductItemPath: string | undefined,
  triggeringSupplierProductItemDescr: string | undefined,
  defaultTerm: unknown
) {
  const fn = `${mn} _mergeSupplierProductItemDataIntoServicesForKitItems |`
  console.debug('='.repeat(100))
  assertIsNotUndefined(servicesForKitItems, fn, 'servicesForKitItems')
  if (!supplierProductWithItems) {
    console.debug(
      `${fn} supplierProductWithItems is falsey.` +
        ` Skipping merge, setting supplierProductItemAvailabilityId to` +
        ` 'Not Avaialble and returning servicesForKitItems.`
    )
    servicesForKitItems.forEach((serviceForKitItem) => {
      serviceForKitItem.itemAvailabilityState = getItemAvailabilityStateById(
        ItemAvailabilityIds.NOT_AVAILABLE
      )
    })
    return servicesForKitItems
  }
  console.debug(`${fn} supplierProductWithItems:`, supplierProductWithItems)

  const remainingItems = [...supplierProductWithItems.items]
  for (const serv of servicesForKitItems) {
    console.debug(
      `${fn} trying to match serviceForKitItem '${serv.product.descr}:`,
      serv
    )
    if (isKitProduct(serv.product)) {
      console.debug(`${fn} ${serv.product.descr} is a kit. No merge required.`)
      continue
    }
    const matchIndex = remainingItems.findIndex(
      (item: ISupplierProductItem) => item.product.id === serv.product.id
    )
    const supplierProductItem =
      matchIndex >= 0 ? remainingItems.splice(matchIndex, 1)[0] : undefined

    console.debug(`${fn} found match:`, supplierProductItem)

    // Note: supplierProductItem may be undefined here. This
    // happens if a supplier's supplierProduct does not include items
    // for all product items in a product
    // kit. If that's the case, _getSupplierProductItemDataToMerge below
    // will return valid 'nulled out' data to use in Object.assign
    // that follows.

    const dataFromSupplierProductItem =
      await _getSupplierProductItemDataToMerge(
        serv,
        supplierProductItem,
        supplierProductOptionalItemsToInclude,
        parentSupplierProductItemPath,
        triggeringSupplierProductItemDescr,
        defaultTerm
      )
    if (dataFromSupplierProductItem) {
      let serviceForKitItem = servicesForKitItems.find((s) => s.id === serv.id)
      assertIsNotUndefined(serviceForKitItem)
      if (doOverrideSupplierProductItemKitQuantity(kitService)) {
        console.debug(
          `${fn} doOverrideSupplierProductItemKitQuantity is true.` +
            ` Deleting kitQuantity property (${dataFromSupplierProductItem.kitQuantity})` +
            ` from supplierProductItem.`
        )
        delete dataFromSupplierProductItem.kitQuantity
      }
      serviceForKitItem = Object.assign(
        serviceForKitItem,
        dataFromSupplierProductItem
      )
    }
  }
  console.debug('='.repeat(100))
  return servicesForKitItems
}

/**
 * Did the user take this supplier-product item, reached by this path? Picks are
 * matched on the same `_selectUid` collectOptionalItemsWithContext gives each
 * option: the item's id plus the path of items that led to it.
 */
function _isOptionPicked(
  supplierProductOptionalItemsToInclude: ISelectItem[] | undefined,
  supplierProductItem: ISupplierProductItem,
  parentSupplierProductItemPath: string | undefined
): boolean {
  const selectUid = `${supplierProductItem.id}__${
    parentSupplierProductItemPath || 'root'
  }`
  return !!supplierProductOptionalItemsToInclude?.some(
    (optionalItem: any) => optionalItem._selectUid === selectUid
  )
}

async function _getSupplierProductItemDataToMerge(
  serviceForKitItem: TMSServiceWithOmits,
  supplierProductItem: ISupplierProductItem | undefined,
  supplierProductOptionalItemsToInclude: ISelectItem[],
  parentSupplierProductItemPath: string | undefined,
  triggeringSupplierProductItemDescr: string | undefined,
  defaultTerm: unknown
) {
  const fn = `${mn} _getSupplierProductItemDataToMerge |`
  console.debug(
    `${fn} itemAvailabilityState calc...` +
      ` !supplierProductItem = ${supplierProductItem},` +
      ` !supplierProductItem?.isOptional = ${!supplierProductItem?.isOptional},` +
      ` supplierProductOptionalItemsToInclude =`,
    supplierProductOptionalItemsToInclude
  )

  const itemAvailabilityState = !supplierProductItem
    ? getItemAvailabilityStateById(ItemAvailabilityIds.NOT_AVAILABLE)
    : !supplierProductItem.isOptional
    ? getItemAvailabilityStateById(ItemAvailabilityIds.MANDATORY)
    : _isOptionPicked(
        supplierProductOptionalItemsToInclude,
        supplierProductItem,
        parentSupplierProductItemPath
      )
    ? getItemAvailabilityStateById(ItemAvailabilityIds.OPTIONAL_INCLUDED)
    : getItemAvailabilityStateById(ItemAvailabilityIds.OPTIONAL_NOT_INCLUDED)

  if (!supplierProductItem) {
    return {
      supplier: serviceForKitItem.supplier,
      tsd: serviceForKitItem.tsd,
      supplierProductItem,
      itemAvailabilityState,
      descr: serviceForKitItem.product.descr,
      recurs: { id: 5, label: 'N/A' },
      paid: { id: 5, label: 'N/A' },
      term: '--',
      kitQuantity: 0,
      splitFraction: 0,
      unitRcAmount: 0,
      mrcAmount: 0,
      unitNrcAmount: 0,
      nrcAmount: 0,
      oneTimeAmount: 0,
      tcvAmount: 0,
      doMultiplyNrcByQuantity: false,
      quoteDate: undefined,
    }
  }

  let childSupplierProductItem: ISupplierProductItem | undefined

  if (supplierProductItem.childSupplierProduct) {
    console.debug(
      `${fn} getting child supplierProductWithItemsPropertyPopulated for:`,
      supplierProductItem.childSupplierProduct
    )
    console.debug('BEFORE GET 2')
    const childSupplierProductWithItems =
      await getSupplierProductWithItemsPropertyPopulated({
        id: supplierProductItem.childSupplierProduct.id,
        label: supplierProductItem.childSupplierProduct.label
          ? supplierProductItem.childSupplierProduct.label
          : 'no label', // TEMPORARY. JR.
      })
    console.debug('AFTER GET 2')

    if (
      !childSupplierProductWithItems.items ||
      childSupplierProductWithItems.items.length < 1
    ) {
      addNotification({
        type: 'warning',
        message:
          `SupplierProduct '${childSupplierProductWithItems.product.descr}'` +
          ` doesn't have an associated supplierProductItem.`,
      })
      return undefined
    }

    childSupplierProductItem = childSupplierProductWithItems.items[0]
    console.debug(`${fn} child supplierProductItem:`, childSupplierProductItem)
  }

  return buildSupplierProductItemDataToMerge({
    serviceForKitItem,
    supplierProductItem: childSupplierProductItem || supplierProductItem,
    itemAvailabilityState,
    triggeringSupplierProductItemDescr: childSupplierProductItem
      ? supplierProductItem.descr
      : triggeringSupplierProductItemDescr,
    defaultTerm,
  })
}

/**
 * The whole SPI -> msService field mapping, in one place.
 *
 * Exported because the "set this kit's options" flow builds a single msService
 * from a chosen supplier-product item long after creation, and it has to land
 * on byte-identical fields -- a second copy of this mapping would drift the
 * first time one of these fields changed. Still called from the create-time
 * merge below; the only difference there is that the caller resolves
 * `itemAvailabilityState` from the user's picks rather than passing
 * OPTIONAL_INCLUDED outright.
 */
export function buildSupplierProductItemDataToMerge({
  serviceForKitItem,
  supplierProductItem,
  itemAvailabilityState,
  triggeringSupplierProductItemDescr,
  defaultTerm,
}: {
  serviceForKitItem: TMSServiceWithOmits
  supplierProductItem: ISupplierProductItem
  itemAvailabilityState: IStateItem
  triggeringSupplierProductItemDescr?: string
  // REQUIRED, deliberately: a caller that forgot it would copy the item's
  // null ("None") term straight onto the msService. See resolveMSServiceTerm.
  defaultTerm: unknown
}) {
  const triggeringDescr = triggeringSupplierProductItemDescr
    ?.replace(/ \(Set\)$/, '')
    .replace(/ \(Kit\)$/, '')
  const itemDescr =
    supplierProductItem.descr || supplierProductItem.product.descr
  return {
    supplier: serviceForKitItem.supplier,
    tsd: serviceForKitItem.tsd,
    supplierProductItem,
    itemAvailabilityState,
    descr: triggeringDescr ? `${triggeringDescr} | ${itemDescr}` : itemDescr,
    recurs: supplierProductItem.recurs,
    paid: supplierProductItem.paid,
    term: resolveMSServiceTerm({
      supplierProductItemTerm: supplierProductItem.term,
      defaultTerm,
    }),
    splitFraction: supplierProductItem.splitFraction,
    kitQuantity: supplierProductItem.quantity,
    unitRcAmount: supplierProductItem.doCopyDollarAmountsToServices
      ? supplierProductItem.unitRcAmount
      : 0,
    unitNrcAmount: supplierProductItem.doCopyDollarAmountsToServices
      ? supplierProductItem.unitNrcAmount
      : 0,
    oneTimeAmount: supplierProductItem.doCopyDollarAmountsToServices
      ? supplierProductItem.oneTimeAmount
      : 0,
    tcvAmount: 0, // gets calculated on display
    doMultiplyNrcByQuantity: supplierProductItem.doMultiplyNrcByQuantity,
    quoteDate: supplierProductItem.quoteDate,
  }
}

// ==================================================================

// Helper Functions -------------------------------------------------

export async function getKitItems({
  callingMn,
  product,
}: {
  callingMn: string
  product: TProduct
}) {
  const fn = `${callingMn} --> ${mn} getKitItems |`
  assertIsDefined(product, fn, 'product')
  console.debug(`${fn} Getting kit items for ${product.descr}...`)
  try {
    const listData = await getKitItemsForProduct(product.id)
    console.dir(listData)
    return listData
  } catch (error) {
    console.error(`${fn} error -`, error)
    throw error
  }
}

export async function getSupplierProductWithItemsPropertyPopulated(
  supplierProduct: ISelectItem
) {
  const fn = `${mn} getSupplierProductWithItemsPropertyPopulated`
  console.debug(
    `${fn} Getting supplierProduct '${supplierProduct.label} (${supplierProduct.id})' with items...`
  )

  try {
    assertIsString(supplierProduct.id)
    const compositeData: ISupplierProductWithItemsPropertyPopulated =
      await getSupplierProductWithItems(supplierProduct.id)
    console.dir(compositeData)
    return compositeData
  } catch (error) {
    console.error(`${fn} error -`, error)
    throw error
  }
}

export function doOverrideSupplierProductItemKitQuantity(
  kitService: TMSServiceWithOmits
) {
  return kitService.product.doExcludeKitServiceWhenBuildingServices &&
    kitService.parentMSServiceId === undefined
    ? kitService.quantity
    : undefined
}
